import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { blockApi, vehicleApi, formatApiError } from '../services/api';
import { resolveStorageUrl } from '../services/storage';
import { toast } from 'sonner';
import { Wrench, Trash, Receipt, Paperclip, X as XIcon } from '@phosphor-icons/react';

export const BLOCK_TYPES = [
  { value: 'werkstatt',   label: 'Werkstatt / Reparatur',     color: '#525866' },
  { value: 'wartung',     label: 'Wartung / Inspektion',      color: '#FFB800' },
  { value: 'leerfahrt',   label: 'Leerfahrt / Überführung',   color: '#2E62FF' },
  { value: 'probefahrt',  label: 'Probefahrt',                color: '#8B5CF6' },
  { value: 'eigenbedarf', label: 'Eigenbedarf / Privatfahrt', color: '#0EA5E9' },
  { value: 'reinigung',   label: 'Reinigung / Aufbereitung',  color: '#10B981' },
  { value: 'reserviert',  label: 'Reserviert / Geblockt',     color: '#525866' },
  { value: 'sonstige',    label: 'Sonstige',                  color: '#8A94A6' },
];
export const BLOCK_TYPE_MAP = Object.fromEntries(BLOCK_TYPES.map((t) => [t.value, t]));

const empty = {
  vehicle_id: '',
  block_type: 'werkstatt',
  start_date: '',
  end_date: '',
  notes: '',
  mileage_start: '',
  mileage_end: '',
};

/**
 * Dialog to create or edit an internal vehicle block (werkstatt, leerfahrt, ...).
 *
 * Props:
 *  - open, onOpenChange
 *  - editing? — existing block (for edit mode)
 *  - prefill? — { vehicle_id, start_date, end_date } for new block from calendar
 *  - onSaved() — refresh callback
 */
const BlockDialog = ({ open, onOpenChange, editing, prefill, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    vehicleApi.getAll().then((r) => setVehicles(r.data || [])).catch(() => {});
    if (editing) {
      setForm({
        vehicle_id: editing.vehicle_id || '',
        block_type: editing.block_type || 'werkstatt',
        start_date: (editing.start_date || '').slice(0, 16),
        end_date: (editing.end_date || '').slice(0, 16),
        notes: editing.notes || '',
        mileage_start: editing.mileage_start ?? '',
        mileage_end: editing.mileage_end ?? '',
      });
    } else {
      setForm({
        ...empty,
        vehicle_id: prefill?.vehicle_id || '',
        start_date: prefill?.start_date || '',
        end_date: prefill?.end_date || '',
      });
    }
  }, [open, editing, prefill]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id || !form.start_date || !form.end_date) {
      toast.error('Fahrzeug, Start- und Endzeit sind erforderlich');
      return;
    }
    if (form.start_date >= form.end_date) {
      toast.error('Endzeit muss nach Startzeit liegen');
      return;
    }
    const payload = {
      vehicle_id: form.vehicle_id,
      block_type: form.block_type,
      start_date: form.start_date.length === 16 ? `${form.start_date}:00` : form.start_date,
      end_date: form.end_date.length === 16 ? `${form.end_date}:00` : form.end_date,
      notes: form.notes || null,
      mileage_start: form.mileage_start === '' ? null : parseInt(form.mileage_start),
      mileage_end: form.mileage_end === '' ? null : parseInt(form.mileage_end),
    };
    try {
      setSaving(true);
      if (editing) {
        await blockApi.update(editing.id, payload);
        toast.success('Belegung aktualisiert');
      } else {
        await blockApi.create(payload);
        toast.success('Belegung angelegt');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err) || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing || !window.confirm('Belegung wirklich löschen?')) return;
    try {
      await blockApi.delete(editing.id);
      toast.success('Belegung gelöscht');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err) || 'Löschen fehlgeschlagen');
    }
  };

  const showKm = ['leerfahrt', 'werkstatt'].includes(form.block_type);
  const canHaveInvoice = editing && ['werkstatt', 'wartung'].includes(editing.block_type);
  const hasInvoice = editing?.invoice_supplier && editing?.invoice_amount != null;

  // Workshop invoice state (used only when editing a werkstatt/wartung block)
  const [invForm, setInvForm] = useState({
    supplier: '',
    amount: '',
    invoice_date: new Date().toISOString().slice(0, 10),
    invoice_number: '',
    notes: '',
    post_to_cashbook: true,
  });
  const [invFile, setInvFile] = useState(null);
  const [invSaving, setInvSaving] = useState(false);

  useEffect(() => {
    if (!open || !editing) {
      setInvForm({ supplier: '', amount: '', invoice_date: new Date().toISOString().slice(0, 10), invoice_number: '', notes: '', post_to_cashbook: true });
      setInvFile(null);
      return;
    }
    if (hasInvoice) {
      setInvForm({
        supplier: editing.invoice_supplier || '',
        amount: editing.invoice_amount != null ? String(editing.invoice_amount) : '',
        invoice_date: editing.invoice_date || new Date().toISOString().slice(0, 10),
        invoice_number: editing.invoice_number || '',
        notes: editing.invoice_notes || '',
        post_to_cashbook: !!editing.cashbook_entry_id,
      });
    }
  }, [open, editing, hasInvoice]);

  const handleAttachInvoice = async () => {
    if (!editing) return;
    if (!invForm.supplier.trim() || !invForm.amount || !invForm.invoice_date) {
      toast.error('Werkstatt, Betrag und Rechnungsdatum sind erforderlich');
      return;
    }
    const fd = new FormData();
    fd.append('supplier', invForm.supplier.trim());
    fd.append('amount', String(parseFloat(invForm.amount)));
    fd.append('invoice_date', invForm.invoice_date);
    if (invForm.invoice_number) fd.append('invoice_number', invForm.invoice_number);
    if (invForm.notes) fd.append('notes', invForm.notes);
    fd.append('post_to_cashbook', invForm.post_to_cashbook ? 'true' : 'false');
    if (invFile) fd.append('file', invFile);
    try {
      setInvSaving(true);
      await blockApi.attachInvoice(editing.id, fd);
      toast.success(invForm.post_to_cashbook ? 'Rechnung gespeichert + ins Kassenbuch gebucht' : 'Rechnung gespeichert');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err) || 'Rechnung konnte nicht angehängt werden');
    } finally {
      setInvSaving(false);
    }
  };

  const handleRemoveInvoice = async () => {
    if (!editing || !window.confirm('Rechnung und zugehörigen Kassenbuch-Eintrag entfernen?')) return;
    try {
      await blockApi.removeInvoice(editing.id);
      toast.success('Rechnung entfernt');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err) || 'Entfernen fehlgeschlagen');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="block-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench weight="fill" className="text-[#E60000]" size={18} />
            {editing ? 'Belegung bearbeiten' : 'Neue interne Belegung'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="overline">Fahrzeug *</Label>
            <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
              <SelectTrigger className="input-swiss" data-testid="block-vehicle-select">
                <SelectValue placeholder="Fahrzeug wählen…" />
              </SelectTrigger>
              <SelectContent>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.brand} {v.model} — {v.license_plate}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="overline">Typ *</Label>
            <Select value={form.block_type} onValueChange={(v) => setForm({ ...form, block_type: v })}>
              <SelectTrigger className="input-swiss" data-testid="block-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BLOCK_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value} data-testid={`block-type-option-${t.value}`}>
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: t.color }} />
                      {t.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {['werkstatt', 'wartung'].includes(form.block_type) && (
              <p className="text-xs text-[#FFB800] mt-1">Wird automatisch im Wartungscenter eingetragen.</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Start *</Label>
              <Input
                type="datetime-local"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="input-swiss"
                required
                data-testid="block-start-input"
              />
            </div>
            <div>
              <Label className="overline">Ende *</Label>
              <Input
                type="datetime-local"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="input-swiss"
                required
                data-testid="block-end-input"
              />
            </div>
          </div>

          {showKm && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">KM Start (optional)</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.mileage_start}
                  onChange={(e) => setForm({ ...form, mileage_start: e.target.value })}
                  className="input-swiss"
                  data-testid="block-mileage-start-input"
                />
              </div>
              <div>
                <Label className="overline">KM Ende (optional)</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.mileage_end}
                  onChange={(e) => setForm({ ...form, mileage_end: e.target.value })}
                  className="input-swiss"
                  data-testid="block-mileage-end-input"
                />
              </div>
            </div>
          )}

          <div>
            <Label className="overline">Notiz</Label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input-swiss w-full px-3 py-2"
              rows={3}
              placeholder="z. B. Bremsenwechsel bei Bosch, Überführung Hauptstandort → Filiale Süd"
              data-testid="block-notes-input"
            />
          </div>

          {canHaveInvoice && (
            <div className="border-t border-[#E2E4E9] pt-4 space-y-3" data-testid="block-invoice-section">
              <div className="flex items-center gap-2">
                <Receipt weight="fill" size={16} className="text-[#FFB800]" />
                <h3 className="text-sm font-bold text-[#111418]">Werkstattrechnung</h3>
                {hasInvoice && (
                  <span className="ml-auto text-xs font-semibold text-[#009951] bg-[#009951]/10 px-2 py-0.5 rounded-sm">
                    angehängt {editing.cashbook_entry_id && '· im Kassenbuch'}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline">Werkstatt / Lieferant *</Label>
                  <Input
                    value={invForm.supplier}
                    onChange={(e) => setInvForm({ ...invForm, supplier: e.target.value })}
                    className="input-swiss"
                    placeholder="z. B. Bosch Service Berlin"
                    data-testid="invoice-supplier-input"
                  />
                </div>
                <div>
                  <Label className="overline">Brutto-Betrag (€) *</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={invForm.amount}
                    onChange={(e) => setInvForm({ ...invForm, amount: e.target.value })}
                    className="input-swiss tabular-nums"
                    data-testid="invoice-amount-input"
                  />
                </div>
                <div>
                  <Label className="overline">Rechnungsdatum *</Label>
                  <Input
                    type="date"
                    value={invForm.invoice_date}
                    onChange={(e) => setInvForm({ ...invForm, invoice_date: e.target.value })}
                    className="input-swiss"
                    data-testid="invoice-date-input"
                  />
                </div>
                <div>
                  <Label className="overline">Rechnungsnummer</Label>
                  <Input
                    value={invForm.invoice_number}
                    onChange={(e) => setInvForm({ ...invForm, invoice_number: e.target.value })}
                    className="input-swiss"
                    placeholder="z. B. RG-2027-1234"
                    data-testid="invoice-number-input"
                  />
                </div>
              </div>
              <div>
                <Label className="overline">Notiz (auf der Rechnung)</Label>
                <Input
                  value={invForm.notes}
                  onChange={(e) => setInvForm({ ...invForm, notes: e.target.value })}
                  className="input-swiss"
                  placeholder="z. B. Bremsbeläge vorne + hinten"
                  data-testid="invoice-notes-input"
                />
              </div>
              <div>
                <Label className="overline">Beleg (PDF/Bild, optional)</Label>
                {hasInvoice && editing.invoice_path && !invFile ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-[#FAFAFB] border border-[#E2E4E9] rounded-sm">
                    <Paperclip size={14} className="text-[#525866]" />
                    <a
                      href={resolveStorageUrl(editing.invoice_path)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-[#E60000] hover:underline truncate flex-1"
                    >
                      {editing.invoice_filename || 'Beleg öffnen'}
                    </a>
                    <label className="text-xs text-[#525866] hover:text-[#E60000] cursor-pointer">
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp"
                        className="hidden"
                        onChange={(e) => setInvFile(e.target.files?.[0] || null)}
                      />
                      ersetzen
                    </label>
                  </div>
                ) : (
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    onChange={(e) => setInvFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-[#525866] file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-[#111418] file:text-white file:font-semibold file:cursor-pointer hover:file:bg-[#000]"
                    data-testid="invoice-file-input"
                  />
                )}
                {invFile && (
                  <div className="flex items-center gap-2 mt-1 text-xs text-[#525866]">
                    <Paperclip size={12} />
                    <span className="truncate flex-1">{invFile.name}</span>
                    <button type="button" onClick={() => setInvFile(null)} className="hover:text-[#E60000]">
                      <XIcon size={12} />
                    </button>
                  </div>
                )}
              </div>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={invForm.post_to_cashbook}
                  onChange={(e) => setInvForm({ ...invForm, post_to_cashbook: e.target.checked })}
                  className="w-4 h-4 accent-[#E60000]"
                  data-testid="invoice-post-cashbook-checkbox"
                />
                <span>Automatisch ins <b>Kassenbuch</b> als Ausgabe verbuchen</span>
              </label>
              <div className="flex gap-2">
                {hasInvoice && (
                  <Button type="button" variant="outline" onClick={handleRemoveInvoice} className="text-[#E60000] border-[#E60000]/30 hover:bg-red-50" data-testid="invoice-remove-btn">
                    <Trash size={14} className="mr-1" /> Rechnung entfernen
                  </Button>
                )}
                <div className="flex-1" />
                <Button type="button" onClick={handleAttachInvoice} disabled={invSaving} className="btn-primary" data-testid="invoice-save-btn">
                  <Receipt size={14} className="mr-1" />
                  {invSaving ? 'Speichere…' : (hasInvoice ? 'Rechnung aktualisieren' : 'Rechnung anhängen')}
                </Button>
              </div>
            </div>
          )}

          {!editing && ['werkstatt', 'wartung'].includes(form.block_type) && (
            <div className="border-t border-[#E2E4E9] pt-3 text-xs text-[#8A94A6]">
              💡 Werkstattrechnung kannst du anhängen, nachdem die Belegung gespeichert ist.
            </div>
          )}

          <DialogFooter className="gap-2">
            {editing && (
              <Button type="button" variant="outline" onClick={handleDelete} className="text-[#E60000] border-[#E60000]/30 hover:bg-red-50">
                <Trash size={14} className="mr-1" /> Löschen
              </Button>
            )}
            <div className="flex-1" />
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" disabled={saving} className="btn-primary" data-testid="block-save-btn">
              {saving ? 'Speichere…' : (editing ? 'Speichern' : 'Anlegen')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BlockDialog;
